import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { formsApi } from '../api/api';
import { Form, FormField, Knowledge } from '../types/form';
import { APP_CONFIG } from '../constants/config';
import { 
  Save, ArrowLeft, Eye, Settings, Menu, X, Palette, Keyboard, Clock, History, Link as LinkIcon
} from 'lucide-react';
import { toast } from 'sonner';
import FormFieldPalette from '../components/FormFieldPalette';
import FormPreview from '../components/FormPreview';
import SortableFormField from '../components/SortableFormField';
import OneUseLinks from '../components/OneUseLinks';
import { initializeFieldOptions } from '../utils/fieldInitialization';
import { normalizeFieldOptions } from '../utils/fieldSaveHandlers';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

interface FormHistory {
  id: string;
  timestamp: number;
  form: Form;
  action: string;
}

interface ValidationError {
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

const FormBuilder: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [form, setForm] = useState<Form>({
    id: '',
    title: '',
    description: '',
    created_by: '',
    created_at: '',
    updated_at: '',
    is_published: false,
    specific_users_only: true,
    shared: false,
    has_public_link: false,
    fields: []
  });
  
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'build' | 'preview' | 'links'>('build');
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [mobileSidebarTab, setMobileSidebarTab] = useState<'settings' | 'palette'>('settings');
  const [isShortcutsModalOpen, setIsShortcutsModalOpen] = useState(false);
  
  const [formHistory, setFormHistory] = useState<FormHistory[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [knowledge, setKnowledge] = useState<Knowledge[]>([]);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  
  interface FormVersion {
    id: string;
    timestamp: Date;
    form: Form;
    description: string;
  }
  const [formVersions, setFormVersions] = useState<FormVersion[]>([]);
  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
  
  const restoreVersion = useCallback((version: FormVersion) => {
    setForm(version.form);
    setHasUnsavedChanges(true);
    setIsVersionHistoryOpen(false);
    toast.success('Version restored', {
      description: `Restored: ${version.description}`
    });
  }, []);
  
  const [activeId, setActiveId] = useState<string | null>(null);
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  
  const autoSaveTimeout = useRef<NodeJS.Timeout | null>(null);
  const originalForm = useRef<Form | null>(null);
  const autoSaveInProgress = useRef<boolean>(false);
  const currentFormRef = useRef<Form | null>(null);
  
  const saveVersion = useCallback((form: Form, description: string) => {
    const newVersion: FormVersion = {
      id: Date.now().toString(),
      timestamp: new Date(),
      form: JSON.parse(JSON.stringify(form)),
      description
    };
    
    setFormVersions(prev => {
      const updated = [newVersion, ...prev];
      return updated.slice(0, 5);
    });
  }, []);

  const fetchForm = useCallback(async () => {
    if (!id || id === 'undefined' || id.trim() === '') {
      return;
    }
    
    if (id === 'new') {
      const newFormData: Form = {
        id: '',
        title: '',
        description: '',
        created_by: '',
        created_at: '',
        updated_at: '',
        is_published: false,
        specific_users_only: true,
        shared: false,
        has_public_link: false,
        fields: []
      };
      
      setForm(newFormData);
      originalForm.current = newFormData;
      currentFormRef.current = newFormData;
      
      const initialHistory: FormHistory = {
        id: Date.now().toString(),
        timestamp: Date.now(),
        form: newFormData,
        action: 'New form created'
      };
      setFormHistory([initialHistory]);
      setHistoryIndex(0);
      setHasUnsavedChanges(false);
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const response = await formsApi.getForm(id);
      const formData: Form = { 
        ...response.data, 
        specific_users_only: true, 
        has_public_link: response.data.has_public_link || false 
      };
      
      setForm(formData);
      originalForm.current = formData;
      currentFormRef.current = formData;
      
      const initialHistory: FormHistory = {
        id: Date.now().toString(),
        timestamp: Date.now(),
        form: formData,
        action: 'Initial load'
      };
      setFormHistory([initialHistory]);
      setHistoryIndex(0);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error fetching form:', error);
      toast.error('Error loading form', {
        description: 'Please try again. If the problem persists, check your connection.'
      });
    } finally {
      setLoading(false);
    }
  }, [id]);

  const addToHistory = useCallback((newForm: Form, action: string) => {
    const historyEntry: FormHistory = {
      id: Date.now().toString(),
      timestamp: Date.now(),
      form: { ...newForm },
      action
    };
    
    const newHistory = formHistory.slice(0, historyIndex + 1);
    newHistory.push(historyEntry);
    
    if (newHistory.length > 50) {
      newHistory.shift();
    } else {
      setHistoryIndex(prev => prev + 1);
    }
    
    setFormHistory(newHistory);
    setHasUnsavedChanges(true);
  }, [formHistory, historyIndex]);

  const validateForm = useCallback((formToValidate: Form): ValidationError[] => {
    const errors: ValidationError[] = [];
    
    if (!formToValidate.title.trim()) {
      errors.push({
        field: 'title',
        message: 'Form title is required',
        severity: 'error'
      });
    }
    
    if (formToValidate.fields.length === 0) {
      errors.push({
        field: 'fields',
        message: 'At least one field is required to publish',
        severity: 'error'
      });
    }
    
    const fieldLabels = formToValidate.fields.map(f => f.label.toLowerCase());
    const duplicates = fieldLabels.filter((label, index) => fieldLabels.indexOf(label) !== index);
    if (duplicates.length > 0) {
      errors.push({
        field: 'fields',
        message: 'Field labels must be unique',
        severity: 'warning'
      });
    }
    
    return errors;
  }, []);

  const triggerAutoSave = useCallback(async () => {
    if (!id || id === 'undefined' || id === 'new' || id.trim() === '' || !hasUnsavedChanges) {
      return;
    }
    
    if (autoSaveInProgress.current) {
      return;
    }
    
    try {
      autoSaveInProgress.current = true;
      setIsAutoSaving(true);
      
      const currentForm = currentFormRef.current || form;
      
      if (!currentForm.fields || currentForm.fields.length === 0) {
        return;
      }
      
      const formData = {
        title: currentForm.title,
        description: currentForm.description,
        slug: currentForm.slug || '',
        is_published: currentForm.is_published,
        specific_users_only: currentForm.specific_users_only,
        shared: currentForm.shared,
        has_public_link: currentForm.has_public_link,
        fields: currentForm.fields.map((field, index) => ({
          ...normalizeFieldOptions(field),
          order: index
        }))
      };
      
      
      await formsApi.updateForm(id, formData);
      setHasUnsavedChanges(false);
      
      saveVersion(currentForm, `Auto-save at ${new Date().toLocaleTimeString()}`);
      
      toast.success('Auto-saved', {
        description: 'Your changes have been automatically saved.',
        duration: 2000,
      });
    } catch (error) {
      console.error('Auto-save failed:', error);
      toast.error('Auto-save failed', {
        description: 'Your changes could not be saved automatically. Please save manually.',
        duration: 4000,
      });
    } finally {
      setIsAutoSaving(false);
      autoSaveInProgress.current = false;
    }
  }, [id, form, hasUnsavedChanges, saveVersion]);

  const updateFormWithHistory = useCallback((updates: Partial<Form>, action: string) => {
    setForm(prev => {
      const newForm = { ...prev, ...updates };
      
      currentFormRef.current = newForm;
      
      addToHistory(newForm, action);
      return newForm;
    });
  }, [addToHistory]);

  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      const historyForm = formHistory[newIndex].form;
      setForm(historyForm);
      currentFormRef.current = historyForm;
      setHasUnsavedChanges(true);
      
    }
  }, [historyIndex, formHistory]);

  const redo = useCallback(() => {
    if (historyIndex < formHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      const historyForm = formHistory[newIndex].form;
      setForm(historyForm);
      currentFormRef.current = historyForm;
      setHasUnsavedChanges(true);
      
    }
  }, [historyIndex, formHistory]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (id && id !== 'undefined' && id.trim() !== '') {
      fetchForm();
    }
  }, [id, fetchForm]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    if (tab && ['build', 'preview', 'links'].includes(tab)) {
      setActiveTab(tab as 'build' | 'preview' | 'links');
    }
  }, [location.search]);

  useEffect(() => {
    const fetchKnowledge = async () => {
      try {
        const response = await formsApi.getKnowledge();
        if (response.data) {
          if ('results' in response.data && Array.isArray(response.data.results)) {
            setKnowledge(response.data.results as Knowledge[]);
          } else if (Array.isArray(response.data)) {
            setKnowledge(response.data as Knowledge[]);
          } else {
            setKnowledge([]);
          }
        } else {
          setKnowledge([]);
        }
      } catch (error) {
        console.error('Error fetching knowledge:', error);
        setKnowledge([]);
      }
    };

    fetchKnowledge();
  }, []);

  useEffect(() => {
    if (hasUnsavedChanges && id && id !== 'undefined' && id !== 'new' && id.trim() !== '') {
      if (autoSaveTimeout.current) {
        clearTimeout(autoSaveTimeout.current);
      }
      
      autoSaveTimeout.current = setTimeout(() => {
        triggerAutoSave();
      }, APP_CONFIG.AUTO_SAVE_TIMEOUT);
    }
    
    return () => {
      if (autoSaveTimeout.current) {
        clearTimeout(autoSaveTimeout.current);
      }
    };
  }, [hasUnsavedChanges, id, triggerAutoSave]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const handleSave = useCallback(async () => {
    // Validate form before saving
    const errors = validateForm(form);
    setValidationErrors(errors);
    
    if (errors.some(e => e.severity === 'error')) {
      toast.error('Please fix errors before saving', {
        description: 'Check the form validation messages and try again.'
      });
      return;
    }

    try {
      setLoading(true);
      
      const formData = {
        title: form.title,
        description: form.description,
        slug: form.slug || '',
        is_published: form.is_published,
        specific_users_only: form.specific_users_only,
        shared: form.shared,
        has_public_link: form.has_public_link,
        collect_ip: form.collect_ip !== false,
        fields: form.fields.map((field, index) => ({
          ...normalizeFieldOptions(field),
          order: index
        }))
      };
      
      if (id && id !== 'new') {
        await formsApi.updateForm(id, formData);
        addToHistory(form, 'Manual save');
        
          saveVersion(form, `Manual save at ${new Date().toLocaleTimeString()}`);
        
        toast.success('Form saved successfully!', {
          description: 'Your form has been saved with all changes.'
        });
      } else {
        const response = await formsApi.createForm(formData);
        const newFormId = response.data?.id;
        
        if (!newFormId) {
          toast.error('Form created but ID not found. Please refresh and try again.');
          return;
        }
        
        setForm(prev => ({ ...prev, id: newFormId }));
        currentFormRef.current = { ...form, id: newFormId };
        
        if (autoSaveTimeout.current) {
          clearTimeout(autoSaveTimeout.current);
        }
        
        setHasUnsavedChanges(false);
        
        toast.success('Form created successfully!', {
          description: 'Your new form has been created.'
        });
        navigate(`/forms/${newFormId}/edit`);
      }
      
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Error saving form', {
        description: 'Please try again. If the problem persists, check your connection.'
      });
    } finally {
      setLoading(false);
    }
  }, [validateForm, form, id, addToHistory, saveVersion, navigate]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
      } else if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      } else if ((e.ctrlKey || e.metaKey) && e.key === '/' && !e.shiftKey) {
        e.preventDefault();
        setIsShortcutsModalOpen(true);
      } else if (e.key === 'Escape') {
        setIsShortcutsModalOpen(false);
        setIsMobileSidebarOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, handleSave]);

  const handleAddField = (fieldType: FormField['field_type']) => {
    let label = `${fieldType.charAt(0).toUpperCase() + fieldType.slice(1)} Field`;
    
    if (fieldType === 'checkbox_with_input') {
      label = 'Checkbox with Input Field';
    } else if (fieldType === 'logic') {
      label = 'Logic Field';
    } else if (fieldType === 'multi_row') {
      label = 'Multi-Row Field';
    }
    
    let newField: FormField = {
      id: `field_${Date.now()}`,
      field_type: fieldType,
      label,
      placeholder: '',
      is_required: false,
      order: form.fields.length,
      options: {},
      validation_rules: {}
    };

    newField = initializeFieldOptions(newField);


    updateFormWithHistory(
      { fields: [...form.fields, newField] },
      `Added ${fieldType} field`
    );
  };

  const handleUpdateField = (fieldId: string, updates: Partial<FormField>) => {
    const updatedFields = form.fields.map(field =>
      field.id === fieldId ? { ...field, ...updates } : field
    );
    
    updateFormWithHistory(
      { fields: updatedFields },
      `Updated field: ${form.fields.find(f => f.id === fieldId)?.label || 'Unknown'}`
    );
  };

  const handleDeleteField = (fieldId: string) => {
    const fieldToDelete = form.fields.find(f => f.id === fieldId);
    const updatedFields = form.fields.filter(field => field.id !== fieldId);
    
    updateFormWithHistory(
      { fields: updatedFields },
      `Deleted field: ${fieldToDelete?.label || 'Unknown'}`
    );
  };


  const handleDragStart = (event: any) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const activeField = form.fields.find(field => field.id === active.id);
      const oldIndex = form.fields.findIndex(field => field.id === active.id);
      const newIndex = form.fields.findIndex(field => field.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const newFields = arrayMove(form.fields, oldIndex, newIndex);
        
        updateFormWithHistory(
          { fields: newFields },
          `Reordered field: ${activeField?.label || active.id}`
        );
      }
    }
    
    setActiveId(null);
  };


  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedFieldsToDelete, setSelectedFieldsToDelete] = useState<Set<string>>(new Set());


  const confirmDeleteSelected = async () => {
    if (selectedFieldsToDelete.size === 0) return;

    const newFields = form.fields.filter(field => !selectedFieldsToDelete.has(field.id));
    const updatedForm = { ...form, fields: newFields };
    
    setForm(updatedForm);
    addToHistory(updatedForm, `Deleted ${selectedFieldsToDelete.size} fields`);
    setSelectedFieldsToDelete(new Set());
    setIsDeleteDialogOpen(false);
    setHasUnsavedChanges(true);
  };

  const togglePublish = async () => {
    if (!id || id === 'undefined' || id.trim() === '') {
      return;
    }
    
    const newPublishState = !form.is_published;
    
    try {
      setLoading(true);
      
      const formData = {
        is_published: newPublishState
      };
      
      
      await formsApi.patchForm(id, formData);
      
      setForm(prev => ({ ...prev, is_published: newPublishState }));
      
      const action = newPublishState ? 'published' : 'unpublished';
      toast.success(`Form ${action} successfully!`, {
        description: newPublishState ? 'Your form is now live and accepting submissions.' : 'Your form is now private and no longer accepting public submissions.'
      });
      
      addToHistory(
        { ...form, is_published: newPublishState },
        `Form ${action}`
      );
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error('Error toggling publish state:', error);
      
      const action = newPublishState ? 'publish' : 'unpublish';
      toast.error(`Error trying to ${action} form`, {
        description: 'Please try again. If the problem persists, check your connection.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="border-b border-border bg-card backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-6">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="-ml-2"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <h1 className="notion-title">
                {id ? 'Edit Form' : 'New Form'}
              </h1>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex border border-border rounded-lg bg-muted p-1">
                <Button
                  variant={activeTab === 'build' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveTab('build')}
                  className="h-8 px-4"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Build
                </Button>
                <Button
                  variant={activeTab === 'preview' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveTab('preview')}
                  className="h-8 px-4"
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </Button>
                <Button
                  variant={activeTab === 'links' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveTab('links')}
                  className="h-8 px-4"
                >
                  <LinkIcon className="w-4 h-4 mr-2" />
                  Custom Links
                </Button>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsShortcutsModalOpen(true)}
                className="h-8 px-3"
                title="Keyboard shortcuts (⌘/)"
              >
                <Keyboard className="w-4 h-4" />
              </Button>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsVersionHistoryOpen(true)}
                className="h-8 px-3"
                title="Version history"
                disabled={formVersions.length === 0}
              >
                <History className="w-4 h-4" />
              </Button>

              <Button
                variant="outline"
                size="sm"
                onClick={togglePublish}
                disabled={!form.is_published && form.fields.length === 0}
                className="h-8 px-4"
                title={!form.is_published && form.fields.length === 0 ? 'Add at least one field before publishing' : ''}
              >
                {form.is_published ? 'Unpublish' : 'Publish'}
              </Button>

              {isAutoSaving && (
                <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                  <div className="animate-spin w-3 h-3 border border-gray-300 dark:border-gray-600 border-t-gray-500 rounded-full"></div>
                  <span className="notion-caption">Auto-saving...</span>
                </div>
              )}

              <Button
                onClick={handleSave}
                disabled={loading}
                size="sm"
                className="h-8 px-4"
              >
                <Save className="w-4 h-4 mr-2" />
                {loading ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12">
        {activeTab === 'build' ? (
          <div className="relative">
            <div className="lg:hidden mb-4">
              <div className="flex items-center justify-between">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
                  className="flex items-center space-x-2"
                >
                  <Menu className="w-4 h-4" />
                  <span>
                    {mobileSidebarTab === 'settings' ? 'Settings' : 'Field Palette'}
                  </span>
                </Button>
                
                <div className="flex border border-border rounded-lg bg-muted p-1">
                  <Button
                    variant={mobileSidebarTab === 'settings' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setMobileSidebarTab('settings')}
                    className="h-8 px-3"
                  >
                    <Settings className="w-4 h-4 mr-1" />
                    Settings
                  </Button>
                  <Button
                    variant={mobileSidebarTab === 'palette' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setMobileSidebarTab('palette')}
                    className="h-8 px-3"
                  >
                    <Palette className="w-4 h-4 mr-1" />
                    Palette
                  </Button>
                </div>
              </div>
            </div>

            {isMobileSidebarOpen && (
              <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setIsMobileSidebarOpen(false)} />
            )}

            <div className={`lg:hidden fixed inset-y-0 left-0 z-50 w-80 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 transform transition-transform duration-300 ease-in-out ${
              isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
            }`}>
              <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-lg font-semibold">
                  {mobileSidebarTab === 'settings' ? 'Form Settings' : 'Field Palette'}
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsMobileSidebarOpen(false)}
                  className=""
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="p-4 overflow-y-auto h-full pb-20">
                {mobileSidebarTab === 'settings' ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label className="notion-label">
                        Form Title
                      </label>
                      <Input
                        value={form.title}
                        onChange={(e) => {
                          setForm(prev => ({ ...prev, title: e.target.value }));
                          // Clear title validation error when user starts typing
                          setValidationErrors(prev => prev.filter(error => error.field !== 'title'));
                        }}
                        placeholder="Enter form title"
                        className={validationErrors.some(e => e.field === 'title') 
                          ? 'border-red-300 focus:border-red-400 focus:ring-red-400/20' 
                          : ''
                        }
                      />
                      {validationErrors.some(e => e.field === 'title') && (
                        <p className="notion-helper text-red-500 mt-1">
                          {validationErrors.find(e => e.field === 'title')?.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="notion-label">
                        Description
                      </label>
                      <textarea
                        className="flex min-h-[80px] w-full rounded-lg border border-input bg-background px-4 py-3 body-medium transition-all placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                        rows={3}
                        value={form.description}
                        onChange={(e) => {
                          setForm(prev => ({ ...prev, description: e.target.value }));
                          setHasUnsavedChanges(true);
                        }}
                        placeholder="Enter form description"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="notion-label">
                        Custom URL Slug (Optional)
                      </label>
                      <Input
                        value={form.slug || ''}
                        onChange={(e) => {
                          const slug = e.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9-_]/g, '-')
                            .replace(/^-+|-+$/g, '');
                          setForm(prev => ({ ...prev, slug }));
                          setHasUnsavedChanges(true);
                        }}
                        placeholder="my-custom-form"
                      />
                      {form.slug ? (
                        <p className="text-xs text-blue-600 mt-1">
                          Public URL: /public/{form.slug}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground mt-1">
                          Leave blank to use UUID-based URL
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="share-form-mobile"
                        checked={form.shared || false}
                        onCheckedChange={(checked) => {
                          setForm(prev => ({ ...prev, shared: checked as boolean }));
                          setHasUnsavedChanges(true);
                        }}
                        className="notion-checkbox"
                      />
                      <label 
                        htmlFor="share-form-mobile"
                        className="notion-label cursor-pointer"
                      >
                        Share Form
                      </label>
                    </div>
                    {form.shared && (
                      <div className="notion-info-box notion-info-box--blue">
                        <p>
                          When enabled, this form will be visible to all users in their "Shared Forms" section and they can edit it.
                        </p>
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="public-link-mobile"
                        checked={form.has_public_link || false}
                        onCheckedChange={(checked) => {
                          setForm(prev => ({ ...prev, has_public_link: checked as boolean }));
                          setHasUnsavedChanges(true);
                        }}
                      />
                      <label
                        htmlFor="public-link-mobile"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        Enable Public Link
                      </label>
                    </div>

                    <div className="flex items-center space-x-2 mt-3">
                      <Checkbox
                        id="collect-ip-mobile"
                        checked={form.collect_ip !== false}
                        onCheckedChange={(checked) => {
                          setForm(prev => ({ ...prev, collect_ip: checked as boolean }));
                          setHasUnsavedChanges(true);
                        }}
                      />
                      <label
                        htmlFor="collect-ip-mobile"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        Collect IP Addresses
                      </label>
                    </div>

                    {form.has_public_link && (
                      <div className="notion-info-box notion-info-box--green">
                        <p>
                          When enabled, this form will have a public link that anyone can use to submit responses.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <FormFieldPalette onAddField={(fieldType) => {
                    handleAddField(fieldType);
                    setIsMobileSidebarOpen(false);
                  }} />
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
              <div className="hidden lg:block lg:col-span-1 space-y-8">
                <Card className="notion-card">
                  <CardHeader className="pb-4">
                    <CardTitle className="notion-section-title">Form Settings</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-8">
                    <div className="space-y-2">
                      <label className="notion-label">
                        Form Title
                      </label>
                      <Input
                        value={form.title}
                        onChange={(e) => {
                          setForm(prev => ({ ...prev, title: e.target.value }));
                          // Clear title validation error when user starts typing
                          setValidationErrors(prev => prev.filter(error => error.field !== 'title'));
                        }}
                        placeholder="Enter form title"
                        className={validationErrors.some(e => e.field === 'title') 
                          ? 'border-red-300 focus:border-red-400 focus:ring-red-400/20' 
                          : ''
                        }
                      />
                      {validationErrors.some(e => e.field === 'title') && (
                        <p className="notion-helper text-red-500 mt-1">
                          {validationErrors.find(e => e.field === 'title')?.message}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="notion-label">
                        Description
                      </label>
                      <textarea
                        className="flex min-h-[80px] w-full rounded-lg border border-input bg-background px-4 py-3 body-medium transition-all placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                        rows={3}
                        value={form.description}
                        onChange={(e) => {
                          setForm(prev => ({ ...prev, description: e.target.value }));
                          setHasUnsavedChanges(true);
                        }}
                        placeholder="Enter form description"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <label className="notion-label">
                        Custom URL Slug (Optional)
                      </label>
                      <Input
                        value={form.slug || ''}
                        onChange={(e) => {
                          const slug = e.target.value
                            .toLowerCase()
                            .replace(/[^a-z0-9-_]/g, '-')
                            .replace(/^-+|-+$/g, '');
                          setForm(prev => ({ ...prev, slug }));
                          setHasUnsavedChanges(true);
                        }}
                        placeholder="my-custom-form"
                      />
                      {form.slug ? (
                        <p className="text-xs text-blue-600 mt-1">
                          Public URL: /public/{form.slug}
                        </p>
                      ) : (
                        <p className="text-xs text-muted-foreground mt-1">
                          Leave blank to use UUID-based URL
                        </p>
                      )}
                    </div>
                    
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="share-form"
                        checked={form.shared || false}
                        onCheckedChange={(checked) => {
                          setForm(prev => ({ ...prev, shared: checked as boolean }));
                          setHasUnsavedChanges(true);
                        }}
                        className="notion-checkbox"
                      />
                      <label 
                        htmlFor="share-form"
                        className="notion-label cursor-pointer"
                      >
                        Share Form
                      </label>
                    </div>
                    {form.shared && (
                      <div className="notion-info-box notion-info-box--blue">
                        <p>
                          When enabled, this form will be visible to all users in their "Shared Forms" section and they can edit it.
                        </p>
                      </div>
                    )}
                    <div className="flex items-start space-x-3">
                      <Checkbox
                        id="public-link"
                        checked={form.has_public_link || false}
                        onCheckedChange={(checked) => {
                          setForm(prev => ({ ...prev, has_public_link: checked as boolean }));
                          setHasUnsavedChanges(true);
                        }}
                        className="notion-checkbox"
                      />
                      <label
                        htmlFor="public-link"
                        className="notion-label cursor-pointer"
                      >
                        Enable Public Link
                      </label>
                    </div>

                    <div className="flex items-start space-x-3 mt-3">
                      <Checkbox
                        id="collect-ip"
                        checked={form.collect_ip !== false}
                        onCheckedChange={(checked) => {
                          setForm(prev => ({ ...prev, collect_ip: checked as boolean }));
                          setHasUnsavedChanges(true);
                        }}
                        className="notion-checkbox"
                      />
                      <label
                        htmlFor="collect-ip"
                        className="notion-label cursor-pointer"
                      >
                        Collect IP Addresses
                      </label>
                    </div>

                    {form.has_public_link && (
                      <div className="notion-info-box notion-info-box--green">
                        <p>
                          When enabled, this form will have a public link that anyone can use to submit responses.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
                <FormFieldPalette onAddField={handleAddField} />
              </div>
              
              <div className="lg:col-span-3">
                <Card className="notion-card">
                  <CardHeader>
                    <CardTitle className="notion-section-title">Form Fields</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loading && (
                      <div className="sr-only" role="status" aria-live="polite">
                        {id ? 'Loading form...' : 'Creating new form...'}
                      </div>
                    )}
                    {!form.fields || form.fields.length === 0 ? (
                      <div className="text-center py-12 text-muted-foreground">
                        <p>No fields added yet. {typeof window !== 'undefined' && window.innerWidth < 1024 ? 'Tap the menu button to add fields.' : 'Drag fields from the palette to get started.'}</p>
                      </div>
                    ) : (
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragStart={handleDragStart}
                        onDragEnd={handleDragEnd}
                      >
                        <SortableContext
                          items={form.fields.map(field => field.id)}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="space-y-6">
                            {form.fields.map((field, index) => (
                              <SortableFormField
                                key={field.id}
                                field={field}
                                index={index}
                                onUpdateField={handleUpdateField}
                                onDeleteField={handleDeleteField}
                                form={form}
                                knowledge={knowledge}
                              />
                            ))}
                          </div>
                        </SortableContext>
                        
                        <DragOverlay>
                          {activeId ? (
                            <SortableFormField
                              field={form.fields.find(f => f.id === activeId)!}
                              index={form.fields.findIndex(f => f.id === activeId)}
                              onUpdateField={handleUpdateField}
                              onDeleteField={handleDeleteField}
                              form={form}
                              knowledge={knowledge}
                            />
                          ) : null}
                        </DragOverlay>
                      </DndContext>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        ) : activeTab === 'preview' ? (
          <FormPreview form={form} />
        ) : activeTab === 'links' ? (
          <div className="max-w-4xl mx-auto">
            {form.id && <OneUseLinks formId={form.id} form={form} />}
          </div>
        ) : null}

        <Dialog open={isShortcutsModalOpen} onOpenChange={setIsShortcutsModalOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <Keyboard className="w-5 h-5" />
                <span>Keyboard Shortcuts</span>
              </DialogTitle>
              <DialogDescription>
                Use these shortcuts to work faster in the form builder.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="space-y-3">
                <h4 className="font-medium text-sm text-foreground">General</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Save form</span>
                    <div className="flex items-center space-x-1">
                      <kbd className="px-2 py-1 text-xs bg-muted rounded border">⌘</kbd>
                      <span className="text-muted-foreground">+</span>
                      <kbd className="px-2 py-1 text-xs bg-muted rounded border">S</kbd>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Show shortcuts</span>
                    <div className="flex items-center space-x-1">
                      <kbd className="px-2 py-1 text-xs bg-muted rounded border">⌘</kbd>
                      <span className="text-muted-foreground">+</span>
                      <kbd className="px-2 py-1 text-xs bg-muted rounded border">/</kbd>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Close modal/sidebar</span>
                    <kbd className="px-2 py-1 text-xs bg-muted rounded border">Esc</kbd>
                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <h4 className="font-medium text-sm text-foreground">Editing</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Undo</span>
                    <div className="flex items-center space-x-1">
                      <kbd className="px-2 py-1 text-xs bg-muted rounded border">⌘</kbd>
                      <span className="text-muted-foreground">+</span>
                      <kbd className="px-2 py-1 text-xs bg-muted rounded border">Z</kbd>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Redo</span>
                    <div className="flex items-center space-x-1">
                      <kbd className="px-2 py-1 text-xs bg-muted rounded border">⌘</kbd>
                      <span className="text-muted-foreground">+</span>
                      <kbd className="px-2 py-1 text-xs bg-muted rounded border">⇧</kbd>
                      <span className="text-muted-foreground">+</span>
                      <kbd className="px-2 py-1 text-xs bg-muted rounded border">Z</kbd>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-3 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  On Windows/Linux, use <kbd className="px-1 text-xs bg-muted rounded">Ctrl</kbd> instead of <kbd className="px-1 text-xs bg-muted rounded">⌘</kbd>
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Selected Fields</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete {selectedFieldsToDelete.size} selected field(s)? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDeleteSelected}>Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <Dialog open={isVersionHistoryOpen} onOpenChange={setIsVersionHistoryOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center space-x-2">
                <History className="w-5 h-5" />
                <span>Version History</span>
              </DialogTitle>
              <DialogDescription>
                Restore a previous version of your form. Only the last 5 versions are kept.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 max-h-96 overflow-y-auto">
              {formVersions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No versions saved yet.</p>
                  <p className="text-sm">Versions are automatically saved when you save or auto-save your form.</p>
                </div>
              ) : (
                formVersions.map((version, index) => (
                  <div
                    key={version.id}
                    className={`border rounded-lg p-4 ${
                      index === 0 ? 'border-primary bg-primary/5' : 'border-border bg-background'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium text-sm">{version.description}</h4>
                          {index === 0 && (
                            <span className="px-2 py-1 text-xs bg-primary/10 text-primary rounded-full">
                              Latest
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>{version.timestamp.toLocaleString()}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            <span>{version.form.fields.length} fields</span>
                          </div>
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {version.form.title || 'Untitled Form'}
                          {version.form.description && (
                            <span className="ml-2">• {version.form.description.substring(0, 50)}{version.form.description.length > 50 ? '...' : ''}</span>
                          )}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => restoreVersion(version)}
                          disabled={index === 0}
                          className="text-xs"
                        >
                          {index === 0 ? 'Current' : 'Restore'}
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            {formVersions.length > 0 && (
              <div className="pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  <strong>Note:</strong> Restoring a version will replace your current form with the selected version. 
                  Make sure to save your current changes if you want to keep them.
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default FormBuilder;